import _, {Dictionary} from 'lodash';
import React, {memo, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IconButton, makeStyles, Menu, MenuItem, TableCell, TableRow} from '@material-ui/core';
import {CatalogProductStatus, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {ModalType, SnackbarType} from '@treez-ui/core/feedback/modals';
import {OptionsIcon} from '@treez-ui/core/icons';
import {ProductValidationResult} from '@treez/commons/jsonSchema/products/product';
import {ProductRegion} from '@treez/commons/jsonSchema/products/regionSchemas';
import {StoreAttributes} from '~/lib/directory';
import {
    closeFeedbackModal,
    openFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import {TreezColor, treezColorMap} from '~/styles/constants';
import {
    addProductsToCatalogs,
    bulkUpdateCatalogProductStatus,
    getCatalogProductData,
    ProductStorePresenceAttributes,
    updateCatalogProduct,
} from '~/utils/ProductAPI';
import StoreMatrixCell from './StoreMatrixCell';
import {useProductFormStateDispatch} from '~/contexts/ProductForm/productFormContext';
import {
    openProductFormOnly,
    openProductInspectorOnly,
} from '~/contexts/ProductForm/productFormActions';
import {informUserErrorRetrievingStoreInfo} from '~/redux/actionCreators/UserFeedbackCreators';
import {getStoreRegion} from '~/lib/entity';
import ProductValidationErrorMessage from '~/modules/widgets/products/ProductValidationErrorMessage';
import {
    getStoreByCatalogId,
    getStoreProductValidationResult,
    StoreProductValidationResult,
} from '~/lib/errors';
import {validateProductWrapper} from '~/lib/product';
import {selectOrganizationCatalogId} from '~/redux/selectors/organizationSelectors';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';

const failedToAddProductTitle = 'Error Adding Product';
const failedToAddProductBody = 'There was an error adding the product to the selected Catalogs';

const failedToRemoveProductTitle = 'Error Adding Product';
const failedToRemoveProductBody =
    'There was an error removing the product to the selected Catalogs';

interface Props {
    className?: string;
    product: ProductInCatalog;
    storePresence: ProductStorePresenceAttributes[];
    stores: StoreAttributes[];
}

export const StoreMatrixRow = (props: Props) => {
    const {product, stores, storePresence} = props;

    const {id: productId, price, msrp} = product;

    const parentOrgPrice = price || msrp || '';

    const dispatch = useDispatch();

    const orgCatalogId = useSelector(selectOrganizationCatalogId);

    const productFormDispatch = useProductFormStateDispatch();

    const [openOptionsMenu, setOpenOptionsMenu] = useState<EventTarget>();
    const [currentStorePresence, updateCurrentStorePresence] = useState(storePresence);

    const storePresenceMap = useMemo(
        () => new Map(currentStorePresence.map((presence) => [presence.id, presence])),
        [currentStorePresence]
    );

    const storeCatalogIds = useMemo(
        () =>
            _.map(stores, (store: StoreAttributes) => {
                if (store.catalogId) {
                    return store.catalogId;
                }
            }) as number[],
        [stores]
    );

    const findCatalogsByStatus = (status: CatalogProductStatus): number[] => {
        const storePresenceThatMatchStatus = _.filter(currentStorePresence, {status});
        return _.map(
            storePresenceThatMatchStatus,
            (storePresence: ProductStorePresenceAttributes) => {
                return storePresence.id;
            }
        );
    };

    const catalogsWithProductInactive: number[] = findCatalogsByStatus(
        CatalogProductStatus.DEACTIVATED
    );

    const catalogsWithProductActive: number[] = findCatalogsByStatus(CatalogProductStatus.ACTIVE);

    const catalogsWithoutProduct = _.filter(storeCatalogIds, (catalog) => {
        const storePresenceCatalogs = _.map(currentStorePresence, (presence) => {
            return presence.id;
        });

        if (_.includes(storePresenceCatalogs, catalog)) {
            return false;
        }
        return true;
    });

    const findProductPresenceInCatalog = (
        catalogId: number
    ): ProductStorePresenceAttributes | undefined => {
        return storePresenceMap.get(catalogId);
    };

    // Prop conditions
    const generateDefaultLoadingStatus = () => {
        const loadStatus = {};
        _.forEach(stores, (store: StoreAttributes) => {
            const {catalogId} = store;
            if (catalogId) {
                loadStatus[catalogId] = false;
            }
        });
        return loadStatus;
    };

    const updateOneCatalogsLoadStatus = (catalogId: number, isLoading: boolean) => {
        setCatalogsLoadStatus({
            ...catalogsLoadStatus,
            [catalogId]: isLoading,
        });
    };

    const changeAllCheckboxLoadingStatus = (loadingStatus: boolean) => {
        const updatedLoadStatus = _.mapValues(catalogsLoadStatus, (catalog) => loadingStatus);

        setCatalogsLoadStatus(updatedLoadStatus);
    };

    // Local State
    const [catalogsLoadStatus, setCatalogsLoadStatus] = useState<Dictionary<boolean>>(
        generateDefaultLoadingStatus()
    );

    // API Call
    const addToAllStores = async () => {
        const storeProductValidationErrorResult: StoreProductValidationResult[] =
            getStoreProductValidationResult(product, catalogsWithoutProduct, stores, dispatch);

        if (storeProductValidationErrorResult.length > 0) {
            openFeedbackModal(closeFeedbackModal, `Not All Product Requirements Are Met`, {
                type: ModalType.ERROR,
                bodyElement: (
                    <>
                        {storeProductValidationErrorResult.map((validationResult, key) => (
                            <ProductValidationErrorMessage
                                key={key}
                                productValidationResult={validationResult.productValidationResult}
                                region={validationResult.region}
                                store={validationResult.store}
                            />
                        ))}
                    </>
                ),
            });
            return;
        }

        try {
            changeAllCheckboxLoadingStatus(true);
            await addProductsToCatalogs(catalogsWithoutProduct, [productId]);
            await bulkUpdateCatalogStatuses(
                catalogsWithProductInactive,
                CatalogProductStatus.ACTIVE
            );
        } catch (error) {
            console.error(error);
            openFeedbackModal(closeFeedbackModal, failedToAddProductTitle, {
                type: ModalType.ERROR,
                bodyText: `${failedToAddProductBody}:
                        ${error.message}`,
            });
        } finally {
            changeAllCheckboxLoadingStatus(false);
        }
    };

    const removeFromAllStores = async () => {
        try {
            changeAllCheckboxLoadingStatus(true);
            await bulkUpdateCatalogStatuses(
                catalogsWithProductActive,
                CatalogProductStatus.DEACTIVATED
            );
        } catch (error) {
            console.error(error);
            openFeedbackModal(closeFeedbackModal, failedToRemoveProductTitle, {
                type: ModalType.ERROR,
                bodyText: `${failedToRemoveProductBody}:
                        ${error.message}`,
            });
        } finally {
            changeAllCheckboxLoadingStatus(false);
        }
    };

    const bulkUpdateCatalogStatuses = async (catalogs: number[], status: CatalogProductStatus) => {
        const catalogProductUpdates = await bulkUpdateCatalogProductStatus(
            orgCatalogId,
            productId,
            status
        );
        const storePresences = catalogProductUpdates.map(
            ({catalogId, price, priceTierId, status}) => ({
                id: catalogId,
                price: String(price),
                priceTierId: priceTierId as number,
                status,
            })
        );

        updateCurrentStorePresence(storePresences);
    };

    const updateSingleStorePresence = async (catalogId: number) => {
        try {
            const presence = await getCatalogProductData([catalogId], [productId]);
            const storePresencesForProduct = presence[productId];
            if (storePresencesForProduct.length > 0) {
                updateCurrentStorePresence([
                    ..._.reject(currentStorePresence, {id: catalogId}),
                    storePresencesForProduct[0],
                ]);
            }
        } catch (error) {
            console.error(error);
            dispatch(informUserErrorRetrievingStoreInfo());
        }
    };

    const handleAddToStore = async (catalogId: number) => {
        const store = getStoreByCatalogId(catalogId, stores);

        if (store !== null) {
            const region = getStoreRegion(store);
            const validationResult: ProductValidationResult = validateProductWrapper(
                product,
                ProductRegion[region!]
            );
            if (validationResult.isValid) {
                try {
                    await addProductsToCatalogs([catalogId], [productId]);
                    await updateSingleStorePresence(catalogId);
                } catch (error) {
                    console.error(error);
                    openFeedbackModal(closeFeedbackModal, `Error Adding Product to Stores`, {
                        type: ModalType.ERROR,
                        bodyText: `There was an error adding products to stores.`,
                    });
                }
            } else {
                openFeedbackModal(closeFeedbackModal, `Not All Product Requirements Are Met`, {
                    type: ModalType.ERROR,
                    bodyElement: (
                        <ProductValidationErrorMessage
                            productValidationResult={validationResult}
                            region={region}
                            store={store}
                        />
                    ),
                });
            }
        } else {
            dispatch(
                showSnackbar({
                    onClose: () => dispatch(clearSnackbar()),
                    type: SnackbarType.WARNING,
                    content: `There isn't a store associated with catalog id ${catalogId}, please contact support@treez.io`,
                })
            );
        }
    };

    const handleActivateOrDeactivate = async (catalogId: number, isChecked: boolean) => {
        const status = isChecked ? CatalogProductStatus.ACTIVE : CatalogProductStatus.DEACTIVATED;

        if (status === CatalogProductStatus.ACTIVE) {
            const store = getStoreByCatalogId(catalogId, stores);

            if (store !== null) {
                const region = getStoreRegion(store);

                const validationResult: ProductValidationResult = validateProductWrapper(
                    product,
                    ProductRegion[region!]
                );

                if (validationResult.isValid === false) {
                    openFeedbackModal(closeFeedbackModal, `Not All Product Requirements Are Met`, {
                        type: ModalType.ERROR,
                        bodyElement: (
                            <ProductValidationErrorMessage
                                productValidationResult={validationResult}
                                region={region}
                                store={store}
                            />
                        ),
                    });
                    return;
                }
            } else {
                dispatch(
                    showSnackbar({
                        onClose: () => dispatch(clearSnackbar()),
                        type: SnackbarType.WARNING,
                        content: `There isn't a store associated with catalog id ${catalogId}, please contact support@treez.io`,
                    })
                );
            }
        }

        try {
            updateOneCatalogsLoadStatus(catalogId, true);
            await updateCatalogProduct(catalogId, productId, {status});
            await updateSingleStorePresence(catalogId);
            updateOneCatalogsLoadStatus(catalogId, false);
        } catch (error) {
            console.error(error);
            openFeedbackModal(closeFeedbackModal, 'Error Updating Product', {
                type: ModalType.ERROR,
                bodyText: `There was an error changing the status of the product:
                        ${error.message}`,
            });
            updateOneCatalogsLoadStatus(catalogId, false);
        }
    };

    const onOptionsMenuActionClicked = (action: () => void) => {
        action();
        // close menu after action is clicked.
        setOpenOptionsMenu(undefined);
    };

    // Lifecycle Methods
    useEffect(() => {
        if (storePresence && storePresence.length > 0) {
            updateCurrentStorePresence(storePresence);
        }
    }, [storePresence]);

    useEffect(() => {
        massActionButton = renderMassAddOrRemoveButton();
    }, [currentStorePresence]);

    // Element Conditions
    const renderMassAddOrRemoveButton = () => {
        if (currentStorePresence == null) {
            return;
        }

        if (
            catalogsWithProductActive.length === currentStorePresence.length &&
            currentStorePresence.length === stores.length
        ) {
            return (
                <MenuItem onClick={() => onOptionsMenuActionClicked(removeFromAllStores)}>
                    Deactivate All
                </MenuItem>
            );
        }
        return (
            <MenuItem onClick={() => onOptionsMenuActionClicked(addToAllStores)}>
                Add to All
            </MenuItem>
        );
    };

    let massActionButton = renderMassAddOrRemoveButton();

    const useStyles = makeStyles({
        massActionCell: {
            width: '120px',
            height: '42px',
            borderLeft: `1px solid ${treezColorMap[TreezColor.GREY_LIGHT]}`,
            paddingRight: '0',
        },
    });

    const {massActionCell} = useStyles();

    return (
        <TableRow>
            <TableCell align="center" className={massActionCell}>
                <div className="mass-action-button-container">
                    <IconButton onClick={(e) => setOpenOptionsMenu(e.target)}>
                        <OptionsIcon />
                    </IconButton>
                    <Menu
                        open={Boolean(openOptionsMenu)}
                        anchorEl={openOptionsMenu as HTMLElement}
                        onClose={() => setOpenOptionsMenu(undefined)}
                    >
                        {massActionButton}
                        <MenuItem
                            onClick={() => {
                                onOptionsMenuActionClicked(() =>
                                    productFormDispatch(openProductFormOnly(product))
                                );
                            }}
                        >
                            Edit Product
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                onOptionsMenuActionClicked(() =>
                                    productFormDispatch(openProductInspectorOnly(product))
                                );
                            }}
                        >
                            View Product
                        </MenuItem>
                    </Menu>
                </div>
            </TableCell>
            {_.map(storeCatalogIds, (catalogId) => {
                const productPresenceInCatalog = findProductPresenceInCatalog(catalogId);
                const storePrice = _.get(productPresenceInCatalog, 'price');
                const storeState: CatalogProductStatus | undefined = _.get(
                    productPresenceInCatalog,
                    'status'
                );
                const isActive = storeState == CatalogProductStatus.ACTIVE;

                return (
                    <StoreMatrixCell
                        key={catalogId}
                        catalogId={catalogId}
                        defaultPrice={parentOrgPrice as string}
                        handleActivateOrDeactivate={handleActivateOrDeactivate}
                        handleAddToStore={handleAddToStore}
                        isLoading={catalogsLoadStatus[catalogId]}
                        productPresenceInCatalog={productPresenceInCatalog}
                        storePrice={storePrice}
                        storeState={isActive}
                        productId={productId}
                    />
                );
            })}
        </TableRow>
    );
};

export default memo(StoreMatrixRow);
